/**
 * Builder's own token-usage summary from GET <studio>/me/usage.
 *
 * Mirrors the shared UsageSummary contract that wg-agents-api returns from
 * GET /agenthome/me/usage and wg-agents-ui renders — keep the shape in sync
 * across both UIs. Studio emphasises the BUILDER ("Building agents") ledger,
 * but the contract is identical.
 */
export interface UsageScope {
  used_tokens: number;
  /** null when unlimited. */
  limit_tokens: number | null;
  /** null when unlimited. */
  remaining_tokens: number | null;
  pct_used: number;
  /** ISO-8601 UTC when this window resets. */
  resets_at: string;
}

/** One cost-center (ledger) slice of a user's usage. The API returns these only
 *  for ledgers the user has spent on or has a ledger-specific cap for. */
export interface UsageLedger {
  cost_center: string;
  unlimited: boolean;
  daily: UsageScope;
  monthly: UsageScope;
}

export interface UsageSummary {
  username: string;
  unlimited: boolean;
  daily: UsageScope;
  monthly: UsageScope;
  /** Per-ledger breakdown (END_USER / BUILDER / SYSTEM). Optional: older API
   *  responses omit it, so the widget hides the breakdown when it's absent. */
  ledgers?: UsageLedger[];
  as_of: string;
}

/** Creator-facing label for a cost-center. Studio is a build-time surface, so
 *  the BUILDER ledger reads "Building agents (studio)" to disambiguate it from
 *  the same ledger shown in the end-user Agent Hub. */
export const LEDGER_LABEL: Record<string, string> = {
  END_USER: 'Using agents',
  BUILDER: 'Building agents (studio)',
  SYSTEM: 'Automation',
};

export function ledgerLabel(costCenter: string): string {
  return LEDGER_LABEL[costCenter] ?? costCenter;
}
